import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Save, Utensils } from "lucide-react";
import {
  upsertFeedingSchema,
  type UpsertFeedingInput,
} from "@shared/validation/feeding.schema";
import { useFeeding, useSaveFeeding } from "../../lib/api/useFeeding";
import { useToast } from "../../lib/toast/ToastProvider";
import { ApiError } from "../../lib/api/client";
import { INPUT_CLASS } from "../../lib/forms/inputClass";
import { SectionCard } from "../ui/SectionCard";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { SECTION_ACCENT } from "../../lib/accents";
import { INLINE_STATUS } from "../../lib/surfaces";

interface FormShape {
  dietType: string;
  restrictions: string;
  likes: string;
  dislikes: string;
  portionNotes: string;
  scheduleNotes: string;
  bottleFormula: string;
  utensilsNotes: string;
}

const EMPTY: FormShape = {
  dietType: "",
  restrictions: "",
  likes: "",
  dislikes: "",
  portionNotes: "",
  scheduleNotes: "",
  bottleFormula: "",
  utensilsNotes: "",
};

const FIELDS: { key: keyof FormShape; label: string; placeholder: string; rows: number }[] = [
  { key: "dietType", label: "Diet Type", placeholder: "e.g., Vegetarian, Dairy-free, Halal", rows: 1 },
  { key: "restrictions", label: "Food Restrictions", placeholder: "Foods to avoid and why", rows: 2 },
  { key: "likes", label: "Likes", placeholder: "Favorite foods and meals", rows: 2 },
  { key: "dislikes", label: "Dislikes", placeholder: "Foods they refuse or dislike", rows: 2 },
  { key: "portionNotes", label: "Portions & Appetite", placeholder: "Typical portion sizes and appetite", rows: 2 },
  { key: "scheduleNotes", label: "Feeding Schedule", placeholder: "Usual meal and snack times", rows: 2 },
  { key: "bottleFormula", label: "Bottle / Formula", placeholder: "Formula brand, amounts, prep instructions", rows: 2 },
  { key: "utensilsNotes", label: "Utensils & Setup", placeholder: "Preferred cups, utensils, seating, help needed", rows: 2 },
];

function nullToEmpty(value: string | null): string {
  return value ?? "";
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function FeedingSection({ childId }: { childId: string }) {
  const { data: feeding, isLoading, error } = useFeeding(childId);
  const saveMutation = useSaveFeeding(childId);
  const { publish } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormShape>({
    resolver: zodResolver(upsertFeedingSchema),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (feeding) {
      reset({
        dietType: nullToEmpty(feeding.dietType),
        restrictions: nullToEmpty(feeding.restrictions),
        likes: nullToEmpty(feeding.likes),
        dislikes: nullToEmpty(feeding.dislikes),
        portionNotes: nullToEmpty(feeding.portionNotes),
        scheduleNotes: nullToEmpty(feeding.scheduleNotes),
        bottleFormula: nullToEmpty(feeding.bottleFormula),
        utensilsNotes: nullToEmpty(feeding.utensilsNotes),
      });
    }
  }, [feeding, reset]);

  const onSubmit = async (data: FormShape) => {
    const payload: UpsertFeedingInput = {
      dietType: emptyToNull(data.dietType),
      restrictions: emptyToNull(data.restrictions),
      likes: emptyToNull(data.likes),
      dislikes: emptyToNull(data.dislikes),
      portionNotes: emptyToNull(data.portionNotes),
      scheduleNotes: emptyToNull(data.scheduleNotes),
      bottleFormula: emptyToNull(data.bottleFormula),
      utensilsNotes: emptyToNull(data.utensilsNotes),
    };
    try {
      await saveMutation.mutateAsync(payload);
      publish({ variant: "success", title: "Feeding & diet saved successfully" });
    } catch (err) {
      publish({
        variant: "error",
        title: "Failed to save feeding & diet",
        description: err instanceof ApiError ? err.message : undefined,
      });
    }
  };

  return (
    <SectionCard icon={Utensils} title="Feeding & Diet" accent={SECTION_ACCENT.feeding}>
      {isLoading ? (
        <div role="status" className={INLINE_STATUS}>
          <Spinner size="sm" />
          Loading feeding & diet…
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">Failed to load feeding &amp; diet: {error.message}</p>
      ) : (
        <>
          <p className="text-sm text-neutral-600">
            Everything a caregiver needs at mealtime — what to serve, what to avoid, and how.
          </p>

          {/* Two columns of fields on a wide profile page; one on a phone. */}
          <form onSubmit={handleSubmit(onSubmit)} className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
            {FIELDS.map(({ key, label, placeholder, rows }) => (
              <div key={key}>
                <label htmlFor={`feeding-${key}`} className="text-sm font-medium text-neutral-800">
                  {label}
                </label>
                <textarea
                  id={`feeding-${key}`}
                  rows={rows}
                  placeholder={placeholder}
                  {...register(key)}
                  className={INPUT_CLASS}
                />
                {errors[key]?.message && (
                  <p className="mt-1 text-sm text-red-600">{errors[key]?.message}</p>
                )}
              </div>
            ))}

            <div className="flex items-center justify-end lg:col-span-2">
              <Button type="submit" disabled={!isDirty} isLoading={isSubmitting || saveMutation.isPending}>
                {saveMutation.isPending ? (
                  "Saving…"
                ) : (
                  <>
                    <Save className="h-4 w-4" aria-hidden />
                    Save Feeding &amp; Diet
                  </>
                )}
              </Button>
            </div>
          </form>
        </>
      )}
    </SectionCard>
  );
}
